import { QueryParams } from './common';
import { Dish, DishAddOn } from './menu';
import { Table } from './table';

export enum OrderType {
  DELIVERY = 'delivery',
  PICKUP = 'pickup',
  DINE_IN = 'dine_in',
  CASHIER = 'cashier',
  TAKEAWAY = 'takeaway',
  DELIVERY_COMPANY = 'delivery_company'
}

export enum OrderStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  PREPARING = 'preparing',
  COMPLETED = 'completed',
  RETURNED = 'returned',
  CANCELLED = 'cancelled',
}

export interface CartItemAddOn {
  id: string;
  cartItemId: string;
  addOnId: string;
  quantity: number;
  addOn?: DishAddOn;
  createdAt?: string;
  updatedAt?: string;
}

export interface OrderItemAddOn {
  id: string;
  orderItemId: string;
  addOnId: string;
  addOnName: string;
  quantity: number;
  unitPrice: number;
  freeQuantity: number;
  totalPrice: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CartItem {
  id: string;
  sessionId: string;
  dishId: string;
  restaurantId: string;
  quantity: number;
  price: number;
  notes?: string;
  dish?: Dish;
  addOns?: CartItemAddOn[];
  createdAt?: string;
  updatedAt?: string;
}

export interface Cart {
  items: CartItem[];
  total: string;
  itemCount: number;
}

export interface OrderItem {
  id: string;
  orderId: string;
  dishId: string;
  dishName: string;
  dishPrice: number;
  dishPreparationTime: number;
  quantity: number;
  notes?: string;
  dish?: Dish;
  addOns?: OrderItemAddOn[];
  createdAt?: string;
  updatedAt?: string;
}

export interface Order {
  id: string;
  orderNumber: string;
  restaurantId: string;
  customerName?: string;
  customerPhone?: string;
  customerAddress?: string;
  orderType: OrderType;
  status: OrderStatus;
  notes?: string;
  totalAmount: number;
  deliveryFee?: number;
  deliveryZoneName?: string;
  deliveryLatitude?: number;
  deliveryLongitude?: number;
  estimatedPreparationTime?: number;
  tableId?: string;
  tableNumber?: string;
  pagerNumber?: string;
  restaurant?: any; // Avoid circular import
  items?: OrderItem[];
  table?: Table;
  createdBy?: { id: string; username: string };
  deliveryCompanyId?: string;
  externalOrderNumber?: string;
  deliveryCompany?: any;
  deliveryCompanyOrder?: any;
  createdAt?: string;
  updatedAt?: string;
}

export interface AddOnInput {
  addOnId: string;
  quantity: number;
}

export interface AddToCartDto {
  dishId: string;
  quantity: number;
  notes?: string;
  addOns?: AddOnInput[];
}

export interface UpdateCartItemDto {
  quantity?: number;
  notes?: string;
  addOns?: AddOnInput[];
}

export interface CreateOrderDto {
  customerName?: string;
  customerPhone?: string;
  customerAddress?: string;
  orderType: OrderType;
  notes?: string;
  tableNumber?: string;
  deliveryLatitude?: number;
  deliveryLongitude?: number;
  deliveryFee?: number;
}

export interface CreatePosOrderDto {
  customerName?: string;
  customerPhone?: string;
  customerAddress?: string;
  deliveryLatitude?: number;
  deliveryLongitude?: number;
  deliveryFee?: number;
  orderType: OrderType;
  notes?: string;
  pagerNumber?: string;
  tableId?: string;
  deliveryCompanyId?: string;
  externalOrderNumber?: string;
  items: {
    dishId: string;
    quantity: number;
    notes?: string;
    addOns?: {
      addOnId: string;
      quantity: number;
    }[];
  }[];
}

export interface UpdateOrderStatusDto {
  status: OrderStatus;
}

export interface OrderQueryParams extends QueryParams {
  status?: OrderStatus;
  orderType?: OrderType;
  isNew?: boolean;
}

export interface OrderStatistics {
  totalOrders: number;
  totalRevenue: number;
  ordersByStatus: any[];
  period: string;
}

export interface DashboardStatistics {
  period: string;
  startDate: string;
  endDate: string;
  currentRevenue: number;
  previousRevenue: number;
  revenueTrend: number;
  totalOrders: number;
  pendingOrders: number;
  confirmedOrders: number;
  preparingOrders: number;
  completedOrders: number;
  returnedOrders: number;
  cancelledOrders: number;
  revenueChart: RevenueChartData[];
  topDishes: TopDish[];
  recentOrders: RecentOrder[];
}

export interface RevenueChartData {
  date: string;
  revenue: number;
}

export interface TopDish {
  id: string;
  name: string;
  image?: string;
  orderCount: number;
  revenue: number;
}

export interface RecentOrder {
  id: string;
  orderNumber: string;
  customerName?: string;
  customerPhone?: string;
  status: string;
  orderType?: OrderType;
  deliveryAddress?: string;
  tableNumber?: string;
  totalAmount: number;
  itemsCount: number;
  createdAt: string;
}
